import React, { Fragment, Suspense, useState } from 'react';

import { AnalysisSet, Component, Member, PlatePair } from '@paragon/contract-library';
import { LineSegment2D, Point2D } from '@paragon/geometry-library';
import {
  getMostCommonPlateSize,
  getMostCommonPlateType,
  getPlateSizeString,
  plateTypeWithTangibleTypesEqualityComparator,
} from '@paragon/material-lists';
import { getAllMembers } from '@paragon/relative-geometry';
import { calculateMemberMidline, calculateMemberThreeJsGeometryOrFallback } from '@paragon/three-shared';
import { notNullish } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import { getDistanceFormatterSelector } from '../../store/selectors/getDistanceFormatterSelector';
import { isLivePlatingActive } from '../../store/selectors/livePlatingSelectors';
import { getPlateTypeFormatter } from '../../store/selectors/platePairFormatters';
import { getShouldShowLabel } from '../../store/selectors/viewStatusSelectors';
import { attemptToEliminateOverlap, LabelInfo } from '../adjustForOverlap';
import { calculatePlatePairLabelPositioningYThreshold } from '../util/calculatePlatePairLabelPositioningYThreshold';
import { getLabelSizeFromTextMesh, LabelSize, LabelVisual } from '../util/LabelVisual';
import { BearingVisual } from './BearingVisual';
import { BracingVisual } from './BracingVisual';
import { GussetVisual } from './GussetVisual';
import {
  getInitialMemberLabelPosition,
  getMemberLabelText,
  getRotationForMemberControls,
  MemberVisual,
} from './MemberVisual';
import {
  getInitialCenterPositionOfPlateLabels,
  getPlatePairLabelPositionsFromCenterPosition,
  getPlatePairLabelVisibility,
  PlatePairLabelPositions,
  PlatePairVisual,
} from './PlatePairVisual';
import { PlateShearLineVisual } from './PlateShearLineVisual';
import { SpliceVisual } from './SpliceVisual';

interface LabelSizes {
  memberLabelSizes: { [memberGuid: string]: LabelSize | undefined };
  plateNameLabelSizes: { [platePairGuid: string]: LabelSize | undefined };
  plateTypeLabelSizes: { [platePairGuid: string]: LabelSize | undefined };
  plateSizeLabelSizes: { [platePairGuid: string]: LabelSize | undefined };
}

const createGetMovementSegmentFromPosition = (allowedLeftOrRightShift: number) => (position: Point2D) =>
  new LineSegment2D(
    new Point2D(position.x - allowedLeftOrRightShift, position.y),
    new Point2D(position.x + allowedLeftOrRightShift, position.y),
  );

function createGetMovementSegmentForMember(member: Member, component: Component) {
  const memberMidline = calculateMemberMidline(member, component);
  if (memberMidline) {
    return () => LineSegment2D.fromSegmentObject(memberMidline);
  }

  return createGetMovementSegmentFromPosition(6);
}

function validateLabelInfo(labelInfo: LabelInfo): LabelInfo {
  const { center, size } = labelInfo;
  if (!Number.isFinite(center.x) || !Number.isFinite(center.y))
    throw new Error(`Expected label center to be finite, but found (${center.x},${center.y})`);
  if (!Number.isFinite(size.width) || !Number.isFinite(size.height))
    throw new Error(`Expected label size to be finite, but found (${size.width},${size.height})`);
  return labelInfo;
}

function getLabelInfoForPlatePair(
  platePair: PlatePair,
  labelPositions: PlatePairLabelPositions,
  labelSizes: LabelSizes,
): LabelInfo | null {
  const nameLabelSize = labelSizes.plateNameLabelSizes[platePair.guid];
  const typeLabelSize = labelSizes.plateTypeLabelSizes[platePair.guid];
  const sizeLabelSize = labelSizes.plateSizeLabelSizes[platePair.guid];

  // If all label positions are `null`, then no labels are visible.
  if (!labelPositions.nameLabelPosition && !labelPositions.typeLabelPosition && !labelPositions.sizeLabelPosition)
    return null;

  const labels: { readonly labelCenter: Point2D; readonly labelSize: LabelSize }[] = [];

  for (const [labelCenter, labelSize] of [
    [labelPositions.nameLabelPosition, nameLabelSize] as const,
    [labelPositions.typeLabelPosition, typeLabelSize] as const,
    [labelPositions.sizeLabelPosition, sizeLabelSize] as const,
  ]) {
    // If a label's position is `null`, then it's not visible, so we don't need to consider it.
    if (!labelCenter) continue;

    // If a label's size is `undefined`, then we don't have the necessary information to create the `LabelInfo`.
    if (!labelSize) return null;

    labels.push({ labelCenter, labelSize });
  }

  if (labels.length === 0) throw new Error('Expected at least one label to be present.');

  const minX = Math.min(...labels.map(({ labelCenter, labelSize }) => labelCenter.x - labelSize.width / 2));
  const minY = Math.min(...labels.map(({ labelCenter, labelSize }) => labelCenter.y - labelSize.height / 2));
  const maxX = Math.max(...labels.map(({ labelCenter, labelSize }) => labelCenter.x + labelSize.width / 2));
  const maxY = Math.max(...labels.map(({ labelCenter, labelSize }) => labelCenter.y + labelSize.height / 2));

  return validateLabelInfo({
    id: platePair.guid,
    center: labelPositions.overallPlateLabelPosition,
    size: { width: maxX - minX, height: maxY - minY },
    getMovementSegment: createGetMovementSegmentFromPosition(8),
  });
}

interface Props {
  readonly componentDesignGuid: string;
  readonly component: Component;
  readonly analysisSet: AnalysisSet | null;
  readonly fontSize: number;
}

export function ComponentVisual({ componentDesignGuid, component, analysisSet, fontSize }: Props) {
  const livePlating = useAppSelector(isLivePlatingActive);
  const lastMemberLineSelectionPoint = useAppSelector((state) => state.application.lastMemberLineSelectionPoint);
  const memberLabelOptions = useAppSelector((state) => state.visualOptions.memberLabelOptions);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);
  const visualOptions = useAppSelector((state) => state.visualOptions);
  const shouldShowLabel = useAppSelector(getShouldShowLabel);
  const plateTypeFormatter = useAppSelector(getPlateTypeFormatter);
  const openContext = useAppSelector((state) => state.application.openContext?.type);
  const isInAssemblyContext = openContext === 'station' || openContext === 'schedule-station';

  // The approach of just storing the sizes might not work if labels aren't perfectly centered (e.g., if characters are
  // different heights). However, anecdotally, this does not seem to be an issue.
  const [labelSizes, setLabelSizes] = useState<LabelSizes>({
    memberLabelSizes: {},
    plateNameLabelSizes: {},
    plateTypeLabelSizes: {},
    plateSizeLabelSizes: {},
  });

  const members = getAllMembers(component).map((member) => {
    const memberThreeJsGeometry = calculateMemberThreeJsGeometryOrFallback(
      member,
      component,
      lastMemberLineSelectionPoint,
    );
    const initialLabelPosition = getInitialMemberLabelPosition(memberThreeJsGeometry);
    const memberLabelText = getMemberLabelText(member, component, memberLabelOptions, distanceFormatter);
    const shouldRenderLabel = !livePlating && !!initialLabelPosition && memberLabelText != null;
    const labelSize = labelSizes.memberLabelSizes[member.guid];
    const labelInfo: LabelInfo | null =
      shouldRenderLabel && initialLabelPosition && labelSize
        ? validateLabelInfo({
            id: member.guid,
            center: initialLabelPosition,
            size: labelSize,
            getMovementSegment: createGetMovementSegmentForMember(member, component),
          })
        : null;

    return {
      member,
      memberThreeJsGeometry,
      memberLabelText,
      shouldRenderLabel,
      labelInfo,
    };
  });

  const platePairLabelPositioningYThreshold = calculatePlatePairLabelPositioningYThreshold(component.platePairs);
  const mostCommonPlateType = getMostCommonPlateType(component.platePairs);
  const mostCommonPlateSize = getMostCommonPlateSize(component.platePairs);

  const platePairs = component.platePairs.map((platePair) => {
    const isMostCommonPlateType = mostCommonPlateType
      ? plateTypeWithTangibleTypesEqualityComparator(platePair, mostCommonPlateType)
      : false;
    const isMostCommonPlateSize =
      (mostCommonPlateSize &&
        platePair.length === mostCommonPlateSize.length &&
        platePair.width === mostCommonPlateSize.width) ??
      false;
    const labelVisibility = getPlatePairLabelVisibility(
      shouldShowLabel,
      platePair.guid,
      isMostCommonPlateType,
      isMostCommonPlateSize,
      visualOptions,
    );
    const initialCenterPosition = getInitialCenterPositionOfPlateLabels(
      platePair,
      labelVisibility,
      fontSize,
      platePairLabelPositioningYThreshold,
    );
    const labelPositions = getPlatePairLabelPositionsFromCenterPosition(
      labelVisibility,
      fontSize,
      initialCenterPosition,
    );

    return {
      platePair,
      labelVisibility,
      shouldRenderLabel:
        !livePlating && (labelVisibility.showName || labelVisibility.showType || labelVisibility.showSize),
      labelInfo: getLabelInfoForPlatePair(platePair, labelPositions, labelSizes),
    };
  });

  const allLabelInfoOrNulls = [...members, ...platePairs]
    .filter((object) => object.shouldRenderLabel)
    .map((object) => object.labelInfo);

  const allLabelInfo = allLabelInfoOrNulls.every(notNullish) ? allLabelInfoOrNulls.filter(notNullish) : null;
  const labelPositionsAdjustedForOverlap = allLabelInfo ? attemptToEliminateOverlap(allLabelInfo) : null;

  return (
    <>
      {members.map(({ member, memberThreeJsGeometry, memberLabelText, shouldRenderLabel }) => {
        const memberMidline = calculateMemberMidline(member, component);
        const rotation = getRotationForMemberControls(memberMidline);

        // Position at origin until labels have been adjusted for overlap.
        const labelPosition =
          (shouldRenderLabel && labelPositionsAdjustedForOverlap
            ? labelPositionsAdjustedForOverlap[member.guid]
            : null) ?? Point2D.origin;

        return (
          <Suspense key={member.guid}>
            <MemberVisual
              member={member}
              memberThreeJsGeometry={memberThreeJsGeometry}
              label={
                shouldRenderLabel && memberLabelText != null ? (
                  <LabelVisual
                    onTextSync={(textMesh) => {
                      setLabelSizes((labelSizes) => ({
                        ...labelSizes,
                        memberLabelSizes: {
                          ...labelSizes.memberLabelSizes,
                          [member.guid]: getLabelSizeFromTextMesh(textMesh),
                        },
                      }));
                    }}
                    position={[labelPosition.x, labelPosition.y, 0]}
                    fontSize={fontSize}
                    rotation={rotation}
                    // Hide until labels have been adjusted for overlap
                    visible={!!labelPositionsAdjustedForOverlap}
                  >
                    {memberLabelText}
                  </LabelVisual>
                ) : null
              }
            />
          </Suspense>
        );
      })}
      {platePairs.map(({ platePair, labelVisibility, shouldRenderLabel }) => {
        const plateTextColor =
          (mostCommonPlateType && plateTypeWithTangibleTypesEqualityComparator(platePair, mostCommonPlateType)) ||
          !isInAssemblyContext
            ? 0x04ffcd
            : 0xff0000;
        const { showName, showType, showSize } = labelVisibility;

        // Position at origin until labels have been adjusted for overlap.
        const labelsCenter =
          (shouldRenderLabel && labelPositionsAdjustedForOverlap
            ? labelPositionsAdjustedForOverlap[platePair.guid]
            : null) ?? Point2D.origin;
        const { nameLabelPosition, typeLabelPosition, sizeLabelPosition } =
          getPlatePairLabelPositionsFromCenterPosition(labelVisibility, fontSize, labelsCenter);

        return (
          <Fragment key={platePair.guid}>
            <PlatePairVisual
              platePair={platePair}
              labels={
                shouldRenderLabel ? (
                  <>
                    {showName ? (
                      <LabelVisual
                        position={[nameLabelPosition!.x, nameLabelPosition!.y, 0]}
                        fontSize={fontSize}
                        color={plateTextColor}
                        onTextSync={(textMesh) => {
                          setLabelSizes((labelSizes) => ({
                            ...labelSizes,
                            plateNameLabelSizes: {
                              ...labelSizes.plateNameLabelSizes,
                              [platePair.guid]: getLabelSizeFromTextMesh(textMesh),
                            },
                          }));
                        }}
                        // Hide until labels have been adjusted for overlap
                        visible={!!labelPositionsAdjustedForOverlap}
                      >
                        {platePair.name}
                      </LabelVisual>
                    ) : null}
                    {showType ? (
                      <LabelVisual
                        position={[typeLabelPosition!.x, typeLabelPosition!.y, 0]}
                        fontSize={fontSize}
                        color={plateTextColor}
                        onTextSync={(textMesh) => {
                          setLabelSizes((labelSizes) => ({
                            ...labelSizes,
                            plateTypeLabelSizes: {
                              ...labelSizes.plateTypeLabelSizes,
                              [platePair.guid]: getLabelSizeFromTextMesh(textMesh),
                            },
                          }));
                        }}
                        // Hide until labels have been adjusted for overlap
                        visible={!!labelPositionsAdjustedForOverlap}
                      >
                        {plateTypeFormatter(platePair.plateType, platePair.tangiblePlateTypesForComboPlateType)}
                      </LabelVisual>
                    ) : null}
                    {showSize ? (
                      <LabelVisual
                        position={[sizeLabelPosition!.x, sizeLabelPosition!.y, 0]}
                        fontSize={fontSize}
                        color={plateTextColor}
                        onTextSync={(textMesh) => {
                          setLabelSizes((labelSizes) => ({
                            ...labelSizes,
                            plateSizeLabelSizes: {
                              ...labelSizes.plateSizeLabelSizes,
                              [platePair.guid]: getLabelSizeFromTextMesh(textMesh),
                            },
                          }));
                        }}
                        // Hide until labels have been adjusted for overlap
                        visible={!!labelPositionsAdjustedForOverlap}
                      >
                        {getPlateSizeString(platePair)}
                      </LabelVisual>
                    ) : null}
                  </>
                ) : null
              }
            />
            <PlateShearLineVisual platePair={platePair} />
          </Fragment>
        );
      })}

      {analysisSet &&
        Object.values(analysisSet.bracing)
          .flat()
          .map(({ point, direction }, i) => <BracingVisual key={`bracing-${i}`} point={point} direction={direction} />)}

      {component.bearings.map((bearing) => (
        <BearingVisual key={bearing.guid} bearing={bearing} component={component} />
      ))}
      {component.gussets.map((gusset) => (
        <GussetVisual key={gusset.guid} gusset={gusset} />
      ))}
      {component.chordSplices.map((splice) => (
        <SpliceVisual
          key={splice.guid}
          splice={splice}
          component={component}
          componentDesignGuid={componentDesignGuid}
        />
      ))}
    </>
  );
}
